import { SvelteSet } from 'svelte/reactivity';
import type { LineEntry } from '../types/LineEntry';
import type { Chip } from '../lib/exclude';
import { buildBlockParameters, chipKey, parseBlockParameters } from '../lib/exclude';

/**
 * Reactive holder for the exclude widget's chip list + a cached lines catalog.
 * Instantiate once per session. `chips` is authoritative; the store keeps a
 * mirrored `Set<string>` for O(1) `has()` on autocomplete filters.
 */
export class ExcludeStore {
  #keys = new SvelteSet<string>();
  chips = $state<Chip[]>([]);
  linesCatalog = $state<LineEntry[]>([]);

  /** Read-only view of the internal chip-key index, used by `filterExcludeSuggestions` to avoid rebuilding a Set per keystroke. */
  get excludedKeys(): ReadonlySet<string> {
    return this.#keys;
  }

  /** True when `chip` is already in the list. */
  hasChip(chip: Chip): boolean {
    return this.#keys.has(chipKey(chip));
  }

  /** Adds `chip` iff not already present. */
  addChip(chip: Chip): void {
    const chipHash = chipKey(chip);
    if (this.#keys.has(chipHash)) return;
    this.chips = [...this.chips, chip];
    this.#keys.add(chipHash);
  }

  /** Removes the chip whose key matches — no-op when absent. */
  remove(chipHash: string): void {
    if (!this.#keys.has(chipHash)) return;
    this.chips = this.chips.filter((chip) => chipKey(chip) !== chipHash);
    this.#keys.delete(chipHash);
  }

  /** Clears every chip. */
  clear(): void {
    this.chips = [];
    this.#keys.clear();
  }

  /** Replaces the catalog wholesale — usually called once at page load. */
  setCatalog(catalog: LineEntry[]): void {
    this.linesCatalog = catalog;
  }

  /**
   * Replaces the chip list (and rebuilds the key index) from `block_modes` +
   * `block_lines` URL CSVs. Discards any chips added before this call.
   */
  restoreFromUrl(modesCsv: string, linesCsv: string): void {
    const restored = parseBlockParameters(modesCsv, linesCsv, this.linesCatalog);
    this.chips = restored;
    this.#keys.clear();
    for (const chip of restored) this.#keys.add(chipKey(chip));
  }

  /**
   * Fills in the `mode` on every line chip whose mode is empty, using
   * `catalog`. Preserves order + additions the user has made since the
   * initial (catalog-free) restore. Backfilling can land two chips on one
   * `chipKey`; the first of each key is kept so `chips` and the key index agree.
   */
  backfillLineModes(catalog: LineEntry[]): void {
    // eslint-disable-next-line svelte/prefer-svelte-reactivity -- throwaway local Map for lookup; not reactive state
    const byName = new Map(catalog.map((entry) => [entry.name, entry]));
    const filled = this.chips.map((chip) => {
      if (chip.kind !== 'line' || chip.mode) return chip;
      const match = byName.get(chip.name);
      return match ? { ...chip, mode: match.mode } : chip;
    });
    const seen = new SvelteSet<string>();
    const updated = filled.filter((chip) => seen.size < seen.add(chipKey(chip)).size);
    this.chips = updated;
    this.#keys.clear();
    for (const chip of updated) this.#keys.add(chipKey(chip));
  }

  /** `{modes, lines}` CSVs for the outgoing `/v1/routes` query and URL. */
  toBlockParams(): { modes: string; lines: string } {
    return buildBlockParameters(this.chips);
  }
}
